
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useBooking } from '@/contexts/BookingContext';
import Header from '@/components/sections/Header';
import AboutSection from '@/components/sections/AboutSection';
import JourneySection from '@/components/sections/JourneySection';
import TestimonialsSection from '@/components/sections/TestimonialsSection';
import StructureSection from '@/components/sections/StructureSection';
import VideoSection from '@/components/sections/VideoSection';
import BlogSection from '@/components/sections/BlogSection';
import LocationSection from '@/components/sections/LocationSection';
import Footer from '@/components/sections/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const SobrePage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { handleWhatsAppOpen } = useBooking();

  const scrollToSection = (id: string) => {
    setIsMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Sobre o Dr. Luis Brunello | Cirurgião Plástico em Curitiba</title>
        <meta name="description" content="Conheça o Dr. Luis Brunello, cirurgião plástico especialista em contorno corporal e cirurgias da mama em Curitiba. Formado pela UNESP, CRM-PR 40.350." />
        <link rel="canonical" href="https://luisbrunello.com.br/sobre" />
        <meta property="og:title" content="Sobre o Dr. Luis Brunello | Cirurgião Plástico em Curitiba" />
        <meta property="og:description" content="Conheça o Dr. Luis Brunello, cirurgião plástico especialista em contorno corporal e cirurgias da mama em Curitiba. Formado pela UNESP, CRM-PR 40.350." />
        <meta property="og:url" content="https://luisbrunello.com.br/sobre" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Dr. Luis Brunello" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:image" content="https://luisbrunello.com.br/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sobre o Dr. Luis Brunello | Cirurgião Plástico em Curitiba" />
        <meta name="twitter:description" content="Conheça o Dr. Luis Brunello, cirurgião plástico especialista em contorno corporal e cirurgias da mama em Curitiba." />
        <meta name="twitter:image" content="https://luisbrunello.com.br/og-image.jpg" />
      </Helmet>

      <Header
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        scrollToSection={scrollToSection}
        onOpenBooking={() => handleWhatsAppOpen()}
      />

      <main>
        {/* Banner interno */}
        <section
          aria-labelledby="sobre-page-title"
          className="relative flex items-center justify-center text-center overflow-hidden"
          style={{ minHeight: '320px', paddingTop: '96px' }}
        >
          {/* Background sutil */}
          <div className="absolute inset-0 bg-dark-elevated" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-b from-dark-base/60 via-transparent to-dark-base/40" aria-hidden="true" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] decorative-blur decorative-blur-primary opacity-10 pointer-events-none" aria-hidden="true" />

          <header className="relative z-10 px-6 py-16 md:py-20">
            <p className="subtitle-wide text-gold mb-4 block">Conheça o Cirurgião</p>
            <h1 id="sobre-page-title" className="font-display text-4xl md:text-5xl lg:text-6xl text-light-primary font-normal uppercase tracking-[0.08em]">
              Dr. Luis Brunello
            </h1>
            <div className="mt-5 w-12 h-px mx-auto" style={{ background: 'hsl(var(--color-gold))' }} aria-hidden="true" />
            <p className="mt-5 text-light-muted text-sm md:text-base max-w-md mx-auto leading-relaxed">
              Cirurgião Plástico · Contorno Corporal e Cirurgias da Mama · Curitiba — PR
            </p>
          </header>
        </section>

        <div className="bg-dark-base border-b border-white/5">
          <div className="container mx-auto px-6 lg:px-12 py-4">
            <Breadcrumbs
              className="mb-0"
              items={[
                { label: 'Home', href: '/' },
                { label: 'Sobre' },
              ]}
            />
          </div>
        </div>

        <AboutSection onOpenBooking={() => handleWhatsAppOpen()} />
        <JourneySection />
        <TestimonialsSection />
        <StructureSection />
        <VideoSection />
        <BlogSection />
        <LocationSection onOpenBooking={() => handleWhatsAppOpen()} />
      </main>
      <Footer />
    </>
  );
};

export default SobrePage;
